/**
 * The week as range bars (Phase 10 plan §4.4), and today's high and low.
 *
 * Plain TypeScript with no Svelte in it, the split `lib/chart` and `lib/map`
 * already have: the pairing and the geometry are pure functions a test calls
 * directly, and `WeekBars.svelte` only draws what they return.
 *
 * Dates are the **point's**, not the viewer's. NWS names its periods by the
 * point's local calendar ("Tuesday Night" starts at 6 PM there), so pairing a
 * day with its night by the viewer's date would split them apart for anybody
 * looking at a forecast more than a few zones away.
 */

import type { ForecastPeriod } from '../api/types'
import { DAY, asZoneLocal } from '../time'

/** Seven days, day and night: the same 14 the sidebar has shown since Phase 0. */
export const WEEK_PERIODS = 14

/**
 * One row of the week: a day and the night that follows it on the same local
 * date. Either end can be missing, and a missing end is drawn as absent rather
 * than guessed. After about 6 PM NWS's first period is "Tonight" with no day
 * before it, and the 14th period is a day with no night after it.
 */
export interface DayRow {
  /** NWS's own name for the period that opens the row: "Tonight", "Tuesday". */
  name: string
  day: ForecastPeriod | null
  night: ForecastPeriod | null
  /** The day's temperature. Null when the row has no day, or NWS sent none. */
  hi: number | null
  /** The night's temperature. */
  lo: number | null
  /** The day's `short`, or the night's for a night-only row. */
  text: string
  /** Stable across refreshes of the same forecast: the opener's start. */
  key: string
}

/** The local date at the point, as a day number, or null for a bad timestamp. */
function localDay(iso: string | null, tz: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.floor(asZoneLocal(new Date(t), tz) / DAY)
}

/**
 * NWS sets `isDaytime` on every period. A null is treated as a day, since a
 * row is always opened by a day period or by a night with no day before it,
 * and a period of unknown kind is safest as a row of its own.
 */
function isNight(p: ForecastPeriod): boolean {
  return p.is_daytime === false
}

/** Pair the forecast's periods into rows, in order. */
export function pairDays(
  forecast: readonly ForecastPeriod[],
  tz: string | null | undefined,
): DayRow[] {
  const periods = forecast.slice(0, WEEK_PERIODS)
  const rows: DayRow[] = []
  for (let i = 0; i < periods.length; i++) {
    const p = periods[i]
    if (isNight(p)) {
      rows.push(row(null, p))
      continue
    }
    const next = periods[i + 1]
    const day = localDay(p.start, tz)
    if (next && isNight(next) && day !== null && localDay(next.start, tz) === day) {
      rows.push(row(p, next))
      i++
    } else {
      rows.push(row(p, null))
    }
  }
  return rows
}

function row(day: ForecastPeriod | null, night: ForecastPeriod | null): DayRow {
  const opener = (day ?? night)!
  return {
    name: opener.name ?? '',
    day,
    night,
    hi: day?.temp ?? null,
    lo: night?.temp ?? null,
    text: opener.short ?? '',
    key: opener.start ?? opener.name ?? '',
  }
}

export interface WeekScale {
  min: number
  max: number
}

/** Degrees of padding each side of the week's own range. */
export const SCALE_PAD = 2
/**
 * The narrowest the scale is ever drawn. A week that swings three degrees
 * must not draw full-width bars that read as a big swing.
 */
export const SCALE_MIN_SPAN = 10

/**
 * The week's own scale: the lowest low to the highest high, padded, and never
 * narrower than `SCALE_MIN_SPAN` (widened about its middle). Null when no row
 * has a temperature at all.
 */
export function weekScale(rows: readonly DayRow[]): WeekScale | null {
  const temps = rows.flatMap((r) => [r.hi, r.lo]).filter((t): t is number => t !== null)
  if (temps.length === 0) return null
  let min = Math.min(...temps) - SCALE_PAD
  let max = Math.max(...temps) + SCALE_PAD
  if (max - min < SCALE_MIN_SPAN) {
    const mid = (min + max) / 2
    min = mid - SCALE_MIN_SPAN / 2
    max = mid + SCALE_MIN_SPAN / 2
  }
  return { min, max }
}

/**
 * What a row draws, in percent of the track. A row with both ends is a bar
 * from lo to hi. A row with one end is a tick at that value, because a bar
 * from a low to nothing would be a claim about a high nobody forecast. A row
 * with neither draws nothing.
 */
export type RowMark =
  | { kind: 'bar'; left: number; width: number }
  | { kind: 'tick'; at: number }
  | { kind: 'none' }

export function rowMark(r: DayRow, scale: WeekScale | null): RowMark {
  if (!scale) return { kind: 'none' }
  const pct = (t: number) => ((t - scale.min) / (scale.max - scale.min)) * 100
  if (r.hi !== null && r.lo !== null) {
    // A night warmer than its day happens (a front through at noon). The bar
    // still spans the two; which end is which is in the numbers beside it.
    const a = pct(Math.min(r.hi, r.lo))
    const b = pct(Math.max(r.hi, r.lo))
    return { kind: 'bar', left: a, width: b - a }
  }
  const only = r.hi ?? r.lo
  return only === null ? { kind: 'none' } : { kind: 'tick', at: pct(only) }
}

/**
 * Today's high and low for the hero's "H 77° L 66°", from the forecast.
 *
 * `Current` is an observation and has neither. The high is the first day
 * period's temperature and the low the first night period's, each only if it
 * falls on today's local date at the point. In the evening the first period
 * is "Tonight", so there is a low and no high, and that is the truth: a high
 * "from earlier today" would have to come from somewhere other than NWS's
 * forecast.
 */
export function todayHighLow(
  forecast: readonly ForecastPeriod[],
  tz: string | null | undefined,
  now: Date = new Date(),
): { high: number | null; low: number | null } {
  const today = Math.floor(asZoneLocal(now, tz) / DAY)
  const firstDay = forecast.find((p) => !isNight(p))
  const firstNight = forecast.find(isNight)
  const on = (p: ForecastPeriod | undefined) =>
    p && p.temp !== null && localDay(p.start, tz) === today ? p.temp : null
  return { high: on(firstDay), low: on(firstNight) }
}
